import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { authStorage } from '@/services/storage';
import { useAuth } from '@/contexts/AuthContext';
import { AuthShell } from '@/components/auth/AuthShell';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { AuthHeading, EDITORIAL_FOCUS_RING, FieldLabel, MonoLabel, PillButton } from '@/components/editorial';
import { homeFor } from '@/lib/home-route';
import { consumeReturnPath } from '@/lib/return-path';
import type { LoginResponse } from '@/types/api';

const RESEND_COOLDOWN = 60;

export function MfaVerifyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setUserFromTokens } = useAuth();
  const { t } = useTranslation('auth');

  const state = location.state as { mfaToken?: string; rememberMe?: boolean; method?: 'email' | 'sms' } | null;
  const mfaToken = state?.mfaToken;
  const rememberMe = state?.rememberMe ?? false;
  const method = state?.method ?? 'email';

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!mfaToken) navigate('/login', { replace: true });
  }, [mfaToken, navigate]);

  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN);
    intervalRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || code.length !== 6) return;
    setSubmitting(true);
    try {
      // rememberMe reaches the backend body so it sets the web refresh-cookie
      // lifetime (native ignores it). Token storage goes through authStorage so
      // the web/native split is honored: web keeps the access token in memory
      // and the refresh token is the httpOnly cookie (no JS-visible write);
      // native mirrors both to Preferences.
      const response = await api.mfa.verify({ mfa_token: mfaToken, code, rememberMe });
      if (response.status === 'success' && response.data) {
        // #2324 — the code was right, but this account must choose a new
        // password before any session exists. api.mfa.verify's declared type
        // predates the hold (and api.ts is another plan's file), so read the
        // two fields through the shared LoginResponse shape.
        const held = response.data as NonNullable<LoginResponse['data']>;
        if (held.password_change_required && held.change_token) {
          navigate('/set-new-password', { state: { changeToken: held.change_token, rememberMe }, replace: true });
          return;
        }
        const { tokens, user } = response.data;
        await authStorage.set('auth_token', tokens.accessToken, { remember: rememberMe });
        if (tokens.refreshToken) await authStorage.set('refresh_token', tokens.refreshToken, { remember: rememberMe });
        // Native: persist the trusted-device token (#763) so the next login on
        // this device skips the challenge. Device state, not session state —
        // always durable (remember: true); web never receives this field (the
        // httpOnly cookie carries it).
        if (response.data.trusted_device_token) {
          await authStorage.set('trusted_device_token', response.data.trusted_device_token, { remember: true });
        }
        // Awaited: it hydrates `permissions` from /me before the role home
        // renders (#2342, B-071 pattern).
        await setUserFromTokens(user);
        // #2429: the MFA step is where this sign-in completes, so it lands
        // where the other completions do (AuthContext's landingFor) — the page
        // the person was on when her session ended, else her role home. The
        // consume also clears the stash so it cannot reach the next sign-in.
        // homeFor, not a hardcoded /dashboard: that mis-routes TRFer/admin.
        navigate(consumeReturnPath(user) ?? homeFor(user.role), { replace: true });
      }
    } catch {
      toast({
        variant: 'destructive',
        title: t('mfa.verificationFailedTitle'),
        description: t('mfa.verificationFailedDescription'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!mfaToken || resendCooldown > 0) return;
    try {
      await api.mfa.resend(mfaToken);
      startCooldown();
      toast({
        title: t('mfa.codeResentTitle'),
        description: method === 'sms' ? t('mfa.codeResentSms') : t('mfa.codeResentEmail'),
      });
    } catch {
      toast({ variant: 'destructive', title: t('mfa.resendFailedTitle'), description: t('mfa.resendFailedDescription') });
    }
  };

  if (!mfaToken) return null;

  return (
    <AuthShell
      documentLabel={t('mfa.documentLabel')}
      meta={[{ k: t('mfa.stepLabel'), v: t('mfa.stepValue') }]}
    >
      <header className="flex flex-col gap-4 mb-10">
        <MonoLabel>{t('mfa.eyebrow')}</MonoLabel>
        <AuthHeading>
          <Trans t={t} i18nKey="mfa.title" components={{ em: <em className="not-italic text-stevens-indigo" /> }} />
        </AuthHeading>
        <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
          {method === 'sms' ? t('mfa.sentToPhone') : t('mfa.sentToEmail')}
        </p>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
        <div className="flex flex-col gap-2">
          <FieldLabel htmlFor="mfa-code">{t('mfa.codeLabel')}</FieldLabel>
          <input
            id="mfa-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            className={cn(
              'w-full rounded-xl border border-stevens-rule bg-stevens-surface px-4 py-4 font-mono text-stat tracking-[0.5em] leading-none text-center text-stevens-ink placeholder:text-stevens-ink-placeholder transition-[border-color,box-shadow] duration-150',
              EDITORIAL_FOCUS_RING,
            )}
          />
        </div>

        <div className="flex items-center gap-3 mt-2">
          <PillButton type="submit" disabled={submitting || code.length !== 6}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('mfa.verifying')}
              </>
            ) : (
              <>{t('mfa.verify')} {"→"}</>
            )}
          </PillButton>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0}
            className="font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendCooldown > 0 ? t('mfa.resendIn', { seconds: resendCooldown }) : t('mfa.resendPrompt')}
          </button>
        </div>
      </form>
    </AuthShell>
  );
}
